import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type {
  ConceptoCotizacion,
  CotizacionWorldOffice,
  ResumenCotizaciones,
  SituacionCotizacion,
} from '@/lib/supabase/tipos-bd'
import type { Orden } from '@/shared/ui/Tabla/OrdenTabla'

/**
 * Datos de la sección Cotizaciones.
 *
 * Son 2.616 documentos y creciendo, así que la lista nunca se trae completa:
 * el filtro y el corte van en la consulta, no en el navegador.
 */

export type FiltrosCotizaciones = {
  busqueda: string
  situacion: SituacionCotizacion | 'todas'
  soloSinCliente: boolean
  /** Días hacia atrás. 0 = sin límite. */
  dias: number
}

export const FILTROS_INICIALES: FiltrosCotizaciones = {
  busqueda: '',
  situacion: 'todas',
  soloSinCliente: false,
  dias: 30,
}

/**
 * Cuántas por página.
 *
 * Menos que en clientes o inventario porque cada fila se puede abrir: con la
 * cotización desplegada, cincuenta filas debajo entierran lo que se está
 * mirando.
 */
const POR_PAGINA = 25

/** Columnas por las que se puede ordenar la lista. */
export type CampoCotizaciones =
  | 'numero'
  | 'fecha'
  | 'cliente_nombre'
  | 'total_con_iva'
  | 'conceptos'
  | 'situacion'

export function useCotizacionesWorldOffice(
  filtros: FiltrosCotizaciones,
  orden: Orden<CampoCotizaciones>,
  pagina = 0,
) {
  const consulta = useQuery({
    queryKey: ['cotizaciones-wo', filtros, orden.campo, orden.ascendente, pagina],
    // Se conserva la página anterior mientras carga la nueva, para que la
    // tabla no parpadee a vacío en cada clic.
    placeholderData: keepPreviousData,
    queryFn: async () => {
      let peticion = supabase
        .from('vista_cotizaciones_world_office')
        .select('*', { count: 'exact' })
        .order(orden.campo, { ascending: orden.ascendente, nullsFirst: false })
        .range(pagina * POR_PAGINA, pagina * POR_PAGINA + POR_PAGINA - 1)

      // Desempate: varias cotizaciones comparten fecha, y sin esto cambian de
      // sitio entre páginas.
      if (orden.campo !== 'numero') {
        peticion = peticion.order('numero', { ascending: false })
      }

      if (filtros.situacion !== 'todas') {
        peticion = peticion.eq('situacion', filtros.situacion)
      }

      if (filtros.soloSinCliente) {
        peticion = peticion.eq('es_consumidor_final', true)
      }

      if (filtros.dias > 0) {
        const desde = new Date()
        desde.setDate(desde.getDate() - filtros.dias)
        peticion = peticion.gte('fecha', desde.toISOString().slice(0, 10))
      }

      const termino = filtros.busqueda.trim()
      if (termino) {
        const digitos = termino.replace(/\D/g, '')
        // El número se busca aparte del nombre: quien escribe "2182" busca esa
        // cotización, no un cliente que contenga esos dígitos.
        peticion = digitos
          ? peticion.or(`numero.eq.${digitos},cliente_nombre.ilike.%${termino}%`)
          : peticion.ilike('cliente_nombre', `%${termino}%`)
      }

      const { data, error, count } = await peticion

      if (error) throw new Error(mensajeDeError(error))

      return {
        filas: (data ?? []) as unknown as CotizacionWorldOffice[],
        total: count ?? 0,
      }
    },
  })

  return {
    cotizaciones: consulta.data?.filas ?? [],
    total: consulta.data?.total ?? 0,
    porPagina: POR_PAGINA,
    cargando: consulta.isLoading,
    error: consulta.error?.message ?? null,
  }
}

/** Recuentos de la cabecera. */
export function useResumenCotizaciones() {
  const consulta = useQuery({
    queryKey: ['cotizaciones-wo', 'resumen'],
    staleTime: 60_000,
    queryFn: async (): Promise<ResumenCotizaciones | null> => {
      const { data, error } = await supabase
        .from('vista_cotizaciones_resumen')
        .select('*')
        .maybeSingle()

      if (error) throw new Error(mensajeDeError(error))

      return (data ?? null) as unknown as ResumenCotizaciones | null
    },
  })

  return { resumen: consulta.data ?? null, cargando: consulta.isLoading }
}

/**
 * Los conceptos de una cotización.
 *
 * Se piden solo cuando alguien la abre: traer 23.612 renglones por si acaso
 * sería descargar la base entera para mirar once líneas.
 */
export function useConceptos(idAsiento: number | null) {
  const consulta = useQuery({
    queryKey: ['cotizacion-conceptos', idAsiento],
    enabled: idAsiento !== null,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ConceptoCotizacion[]> => {
      const { data, error } = await supabase
        .from('vista_cotizacion_conceptos')
        .select('*')
        .eq('id_asiento', idAsiento!)
        .order('linea')

      if (error) throw new Error(mensajeDeError(error))

      return (data ?? []) as unknown as ConceptoCotizacion[]
    },
  })

  return {
    conceptos: consulta.data ?? [],
    cargando: consulta.isLoading,
    error: consulta.error?.message ?? null,
  }
}

/**
 * ADOPTAR UN DOCUMENTO DE WORLD OFFICE PARA PODER MODIFICARLO.
 *
 * Modificar trabaja sobre una cotización nuestra, y las que alguien hizo
 * directamente allá no la tienen. Esto la crea —sin orden, como una venta de
 * materiales— y devuelve su id para abrir el cotizador encima (0163).
 */
export function useAdoptarCotizacion() {
  const clienteConsultas = useQueryClient()

  return useMutation({
    mutationFn: async (idAsiento: number): Promise<string> => {
      const { data, error } = await supabase.rpc('fn_adoptar_cotizacion_libre', {
        p_wo_id_asiento: idAsiento,
      })

      if (error) throw new Error(mensajeDeError(error))

      return data as string
    },
    onSuccess: async () => {
      await clienteConsultas.invalidateQueries({ queryKey: ['cotizaciones-wo'] })
    },
  })
}
